import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { OpSelect } from 'new-components/DLS/OpSelect/OpSelect';
import { OpCheckbox } from 'new-components/DLS/OpCheckbox/OpCheckbox';
import { OpDivider } from 'new-components/DLS/OpDivider/OpDivider';
import { OpForm } from 'new-components/DLS/OpForm/OpForm';
import { OpModal } from 'new-components/DLS/OpModal/OpModal';
import { OpSwitch } from 'new-components/DLS/OpSwitch/OpSwitch';
import { useConfirmModal } from 'utils/customHooks/useConfirmModal';
import { useValidateScopes } from 'utils/customHooks/useValidateScopes';
import { getOpParams } from 'utils/getOpParams';
import { heliumReceive } from 'utils/helpers';

import STATUS from '../../../../../../constants/status';
import {
  ConfirmDeniedData,
  DenyReason,
} from '../../../../../../types/denyTypes';
import { formatFullName } from '../../../../../../utils/utils';
import {
  getStatusColor,
  profileIcon,
} from '../../../../../../utils/visitorsHelper';

interface VisitorsModalProps {
  open: boolean;
  newStatus: number;
  visitorList: DeniedVisitor[];
  onConfirm: (data: ConfirmDeniedData) => void;
  onClose: () => void;
}

interface DeniedVisitor {
  id: number;
  firstName?: string | null;
  middleName?: string | null;
  lastName?: string | null;
  status?: number;
  reasonId?: number;
  details?: string;
  addToBlockList?: boolean;
  addToWatchList?: boolean;
}

interface DenyReasonsResponse {
  data?: DenyReason[] | null;
}

const SignInManuallyAccessControlModal = ({
  open,
  newStatus,
  visitorList,
  onConfirm,
  onClose,
}: VisitorsModalProps) => {
  const { t } = useTranslation();

  const { orgId } = getOpParams();
  const { isValid: hasRedFlagWrite } = useValidateScopes([
    'o{orgId}-visitorRedFlag:w',
    'o{orgId}-visitorVisitors:w',
    'o:w',
    's-o:w',
  ]);
  const { isValid: hasVisitorlogWrite } = useValidateScopes([
    'o{orgId}-visitorVisitorLog:w',
    'o{orgId}-visitorVisitors:w',
    'o:w',
    's-o:w',
  ]);

  const [denyReasons, setDenyReasons] = useState<DenyReason[]>([]);
  const [addToBlockList, setAddToBlockList] = useState(false);
  const [addToWatchList, setAddToWatchList] = useState(false);

  const [form] = OpForm.useForm();

  const confirmModal = useConfirmModal();

  const initialValues = { reasonForDenial: denyReasons[0]?.id };

  useEffect(() => {
    const fetchData = async () => {
      const denyReasonPriority = [
        t('visitors.accessControlModal.denyReasons.insufficientId'),
        t('visitors.accessControlModal.denyReasons.blockWatchList'),
        t('visitors.accessControlModal.denyReasons.byHostTeam'),
        t('visitors.accessControlModal.denyReasons.appointmentIssues'),
        t('visitors.accessControlModal.denyReasons.behaviorIssues'),
        t('visitors.accessControlModal.denyReasons.safetyConcerns'),
        t('visitors.accessControlModal.denyReasons.other'),
      ];
      try {
        const denyReasonsResponse = (await heliumReceive('listAllDenyReason', [
          orgId,
        ])) as unknown as DenyReasonsResponse;
        const orderedDenyReasons = (denyReasonsResponse.data ?? []).sort(
          (a, b) =>
            denyReasonPriority.indexOf(a.name || '') -
            denyReasonPriority.indexOf(b.name || ''),
        );
        setDenyReasons(orderedDenyReasons);
      } catch {
        // Failed to fetch deny reasons
      }
    };
    fetchData();
  }, [open, orgId, t]);

  useEffect(() => {
    if (open && visitorList.length > 0) {
      const checkedIds = visitorList
        .filter((visitor) => visitor.status === STATUS.DENIED_ENTRY.id)
        .map((visitor) => String(visitor.id));

      const deniedVisitor = visitorList.find(
        (visitor) => visitor.status === STATUS.DENIED_ENTRY.id,
      );

      form.setFieldsValue({
        visitorsCheckbox: checkedIds,
        reasonForDenial: deniedVisitor?.reasonId || denyReasons[0].id,
        detail: deniedVisitor?.details || '',
        addToBlockList: deniedVisitor?.addToBlockList || false,
        addToWatchList: deniedVisitor?.addToWatchList || false,
      });
    }
  }, [open, visitorList, denyReasons, form]);

  const visitorOptions = visitorList.map((visitor) => ({
    label: formatFullName(
      visitor.firstName,
      visitor.middleName,
      visitor.lastName,
    ),
    value: `${visitor.id}`,
    firstName: visitor.firstName,
    lastName: visitor.lastName,
    color:
      visitor?.status === STATUS.DENIED_ENTRY.id
        ? getStatusColor('Denied Entry')
        : getStatusColor('Signed In'),
  }));

  const onOkHandler = async () => {
    const selectedVisitors = form.getFieldValue('visitorsCheckbox') || [];
    const reasonId = form.getFieldValue('reasonForDenial');
    const details = form.getFieldValue('detail');
    const addToBlockListValue = form.getFieldValue('addToBlockList');
    const addToWatchListValue = form.getFieldValue('addToWatchList');

    onConfirm({
      selectedVisitors,
      reasonId,
      details,
      addToBlockList: addToBlockListValue,
      addToWatchList: addToWatchListValue,
    });
    onClose();
  };

  const getTitle = () => {
    switch (newStatus) {
      case Number(STATUS.SIGNED_IN.id):
        return t('visitors.accessControlModal.title.signIn');
      case Number(STATUS.SIGNED_OUT.id):
        return t('visitors.accessControlModal.title.signOut');
      case Number(STATUS.DENIED_ENTRY.id):
        return t('signInManuallyDrawer.footer.denyEntry');
      default:
        return '';
    }
  };

  const handleBlockListChange = (checked: boolean) => {
    setAddToBlockList(checked);
    if (checked) {
      setAddToWatchList(false);
      form.setFieldsValue({ addToWatchList: false });
    }
  };

  const handleWatchListChange = (checked: boolean) => {
    setAddToWatchList(checked);
    if (checked) {
      setAddToBlockList(false);
      form.setFieldsValue({ addToBlockList: false });
    }
  };

  const finalOnCancel = () => {
    if (form.isFieldsTouched()) {
      confirmModal({
        title: t('confirmDiscard.title'),
        content: t('confirmDiscard.message'),
        okText: t('confirmDiscard.ok'),
        cancelText: t('confirmDiscard.cancel'),
        onOk: () => {
          form.resetFields();
          setAddToBlockList(false);
          setAddToWatchList(false);
          onClose();
        },
      });
    } else {
      setAddToBlockList(false);
      setAddToWatchList(false);
      onClose();
    }
  };

  return (
    <OpModal
      title={getTitle()}
      open={open}
      onOk={onOkHandler}
      onCancel={finalOnCancel}
      centered
      width={720}
    >
      <OpDivider />

      <OpForm
        form={form}
        initialValues={initialValues}
        hasError={false}
        defaultButtons={false}
        onSubmit={() => {}}
      >
        <OpForm.Item name="visitorsCheckbox">
          <OpCheckbox.Group style={{ width: '100%' }}>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
              }}
            >
              {visitorOptions?.map((option) => (
                <OpCheckbox
                  key={option.value}
                  value={option.value}
                  style={{ marginBottom: '24px' }}
                >
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    {profileIcon({
                      firstName: option.firstName || '',
                      lastName: option.lastName || '',
                      size: 28,
                      color: option.color,
                    })}
                    <span style={{ marginLeft: '12px' }}> {option.label}</span>
                  </div>
                </OpCheckbox>
              ))}
            </div>
          </OpCheckbox.Group>
        </OpForm.Item>
        {newStatus === Number(STATUS.DENIED_ENTRY.id) && (
          <>
            <OpForm.Item
              label={t('visitors.accessControlModal.reasonForDenial')}
              name="reasonForDenial"
            >
              <OpSelect
                options={denyReasons.map((reason) => ({
                  label: t(reason.languageCode ?? '', reason.name ?? ''),
                  value: reason.id,
                }))}
              />
            </OpForm.Item>
            {hasVisitorlogWrite && (
              <OpForm.TextAreaInput
                label={t('visitors.accessControlModal.details')}
                name="detail"
                rows={4}
              />
            )}

            {hasRedFlagWrite && (
              <>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    width: '100%',
                    marginBottom: '24px',
                  }}
                >
                  <OpForm.Item
                    name="addToBlockList"
                    valuePropName="checked"
                    noStyle
                  >
                    <OpSwitch
                      onChange={handleBlockListChange}
                      checked={addToBlockList}
                    />
                  </OpForm.Item>
                  <span style={{ marginLeft: 8 }}>
                    {t('visitors.accessControlModal.addToBlockList')}
                  </span>
                </div>

                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    width: '100%',
                  }}
                >
                  <OpForm.Item
                    name="addToWatchList"
                    valuePropName="checked"
                    noStyle
                  >
                    <OpSwitch
                      onChange={handleWatchListChange}
                      checked={addToWatchList}
                    />
                  </OpForm.Item>
                  <span style={{ marginLeft: 8 }}>
                    {t('visitors.accessControlModal.addToWatchList')}
                  </span>
                </div>
              </>
            )}
          </>
        )}
      </OpForm>
    </OpModal>
  );
};

export default SignInManuallyAccessControlModal;
